import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  useMediaQuery,
  Button,
  Box,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorBox from "../../components/ErrorBox";

export interface ValidationFailureNotificationProps {
  open: boolean;
  onStayInSSML: () => void;
  onSwitchToVisual: () => void;
}

export default function ValidationFailureNotification({
  open,
  onStayInSSML,
  onSwitchToVisual,
}: ValidationFailureNotificationProps): JSX.Element {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery("(min-width: 1025px)");

  return (
    <Dialog
      id="ssml-validation-notification"
      open={open}
      fullScreen
      PaperProps={
        isDesktop
          ? {
              sx: {
                width: 444,
                height: "auto",
              },
            }
          : {}
      }
    >
      <DialogTitle id="ssml-validation-notification-title">
        <Stack direction="row" spacing={2} alignItems="center">
          <WarningIcon
            sx={{
              color: "warning.main",
              width: "35px",
              height: "35px",
            }}
          />
          <Typography variant="h6">
            {t("ssml.ssmlViewBanner.failedValidationHeader")}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{ margin: "8px 24px", padding: 0 }}
        id="ssml-validation-notification-content"
      >
        <Typography variant="body1" sx={{ marginBottom: 1 }}>
          {t("ssml.ssmlViewBanner.failedValidationText")}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "warning.main",
            fontWeight: 700,
            marginTop: 2,
          }}
        >
          {t("ssml.ssmlViewBanner.failedValidationText2")}
        </Typography>
        <Box sx={{ marginTop: 2 }}>
          <ErrorBox isShortened={true}/>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
        id="ssml-validation-notification-actions"
      >
        <Stack direction="row" gap={2}>
          <Button
            onClick={onStayInSSML}
            id="return-to-ssml-button"
            variant="outlined"
            sx={{
              color: "primary.dark",
              borderColor: "primary.main",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              padding: "8px 16px",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "rgba(0, 121, 107, 0.04)",
              },
            }}
          >
            {t("buttons.returntoSSMLView")}
          </Button>
          <Button
            onClick={onSwitchToVisual}
            id="switch-to-visual-button"
            variant="contained"
            sx={{
              backgroundColor: "warning.main",
              color: "text.primary",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              padding: "8px 16px",
              "&:hover": {
                backgroundColor: "#fb8c00",
              },
            }}
          >
            {t("buttons.switchToVisualMode")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
